import { useEffect, useState } from "react";
import { Database, Download, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Field, PageHead, Spinner, useFetch } from "@/components/common";

const Flag = ({ label }) => (
  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
    <span>{label}</span><span className="flex items-center gap-1 text-xs font-bold text-slate-500"><Lock size={12} /> Disabled · Phase 1</span>
  </div>
);

export default function SettingsPage() {
  const { user } = useAuth();
  const { data } = useFetch("/admin/settings");
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF({ org_name: data.org_name || "", support_contact: data.support_contact || "", public_base_url: data.public_base_url || "" }); }, [data]);
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/settings", { ...f, support_contact: f.support_contact || null, public_base_url: f.public_base_url || null }); toast.success("Settings saved"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const exportDb = async () => {
    try {
      const { data: d } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `gia-care-export-${d.exported_at.slice(0, 10)}.json`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!f) return <Spinner />;
  return (
    <div className="max-w-3xl space-y-6">
      <PageHead eyebrow="Settings" title="Application settings" />
      <form onSubmit={save} className="surface space-y-4 p-6" data-testid="settings-form">
        <Field label="Organization name"><input className="field" value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} data-testid="settings-org-input" /></Field>
        <Field label="Support contact"><input className="field" value={f.support_contact} onChange={(e) => setF({ ...f, support_contact: e.target.value })} data-testid="settings-support-input" /></Field>
        <Field label="Public app URL for QR codes" hint="e.g. https://care.yourdomain.com — leave empty to use the current domain">
          <input className="field font-mono" value={f.public_base_url} onChange={(e) => setF({ ...f, public_base_url: e.target.value })} data-testid="settings-baseurl-input" />
        </Field>
        <button className="btn-primary" data-testid="settings-save-button">Save settings</button>
      </form>
      <section className="surface space-y-2 p-6">
        <h2 className="mb-2 text-base font-bold">Production switches</h2>
        <Flag label="Live PayMongo QR Ph payments" /><Flag label="Insurance coverage activation via insurer API" /><Flag label="Prescription issuance" />
      </section>
      {user.role === "super_admin" && (
        <section className="surface p-6">
          <h2 className="flex items-center gap-2 text-base font-bold"><Database size={17} /> Database export</h2>
          <p className="mb-4 mt-1 text-xs text-slate-500">Download all GIA CARE+ collections as portable JSON (password hashes excluded).</p>
          <button className="btn-secondary" onClick={exportDb} data-testid="export-database-button"><Download size={15} /> Export JSON</button>
        </section>
      )}
    </div>
  );
}

export function AuditLog() {
  const { data, loading } = useFetch("/admin/audit?limit=300");
  if (loading && !data) return <Spinner />;
  return (
    <div>
      <PageHead eyebrow="Audit log" title="Audit trail" sub="Every sensitive action is recorded" />
      <div className="surface overflow-x-auto">
        <table className="data-table" data-testid="audit-table">
          <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Entity</th><th>Details</th></tr></thead>
          <tbody>{data?.map((a) => (
            <tr key={a.id}>
              <td className="whitespace-nowrap text-xs">{fmtDateTime(a.created_at)}</td><td className="text-xs"><b>{a.actor_username}</b> · {a.actor_role}</td>
              <td className="font-mono text-[11px] font-semibold">{a.action}</td><td className="font-mono text-[11px]">{a.entity}:{String(a.entity_id).slice(0, 18)}</td>
              <td className="max-w-xs truncate font-mono text-[10px] text-slate-500">{JSON.stringify(a.details)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
